import { cn } from '@/lib/utils';
import {
  CUSTOM_PLACEMENT,
  type GuidedPlacement,
} from '@/stores/guidedEntryStore';

/**
 * Placement chooser for one side of the guided entry.
 *
 * Each option is a shirt pictogram with the design area marked, so the seller
 * reads the placement from the shape rather than the label. Mirrors the source
 * design's four-up grid.
 */

/** Where a preset's design area sits on the 24x24 shirt pictogram. */
interface AreaMark {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Pictogram geometry per placement.
 *
 * Deliberately hand-drawn rather than derived from the preset's units: a preset
 * mixes relative and millimetre boxes against a print area this component does
 * not know, and an icon only has to be recognisable.
 */
const AREA_MARKS: Record<string, AreaMark> = {
  front_center_large: { x: 9, y: 10, w: 6, h: 7 },
  front_center_small: { x: 10, y: 8, w: 4, h: 4 },
  // The pictogram is a front view, so the wearer's left reads as screen right.
  front_left_chest: { x: 12.5, y: 7.5, w: 3, h: 3 },
  front_right_chest: { x: 8.5, y: 7.5, w: 3, h: 3 },
  back_center_large: { x: 9, y: 10, w: 6, h: 7 },
  back_upper: { x: 10, y: 6.5, w: 4, h: 3.5 },
  back_lower: { x: 8.5, y: 15, w: 7, h: 3.5 },
  [CUSTOM_PLACEMENT]: { x: 8, y: 8, w: 8, h: 10 },
};

const SHIRT_OUTLINE =
  'M8 3 H 16 L 21 6 L 19.5 10 L 17 9 V 21 H 7 V 9 L 4.5 10 L 3 6 Z';

export interface PlacementOption {
  id: GuidedPlacement;
  label: string;
}

interface GuidedPlacementPickerProps {
  label: string;
  options: PlacementOption[];
  value: GuidedPlacement;
  onChange: (placement: GuidedPlacement) => void;
}

export function GuidedPlacementPicker({
  label,
  options,
  value,
  onChange,
}: GuidedPlacementPickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <span className="dc-type-label text-[var(--mute)]">{label}</span>
      <div
        className="grid gap-1.5"
        style={{
          // One row for all tiles: narrow tiles leave the frame more room.
          gridTemplateColumns: `repeat(${Math.min(options.length, 5)}, minmax(0,1fr))`,
        }}
      >
        {options.map((option) => {
          const isActive = option.id === value;
          const isCustom = option.id === CUSTOM_PLACEMENT;
          const mark = AREA_MARKS[option.id] ?? AREA_MARKS[CUSTOM_PLACEMENT];

          return (
            <button
              key={option.id}
              type="button"
              title={option.label}
              aria-pressed={isActive}
              onClick={() => onChange(option.id)}
              className={cn(
                'flex min-h-[62px] min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-control px-0.5 py-1.5',
                'transition-[box-shadow,background,color] duration-[120ms]',
                isActive
                  ? 'bg-[var(--paper)] text-[var(--ink)] shadow-[inset_0_0_0_2px_var(--cobalt-500)]'
                  : 'bg-[var(--paper-2)] text-[var(--ink-2)] shadow-[inset_0_0_0_1px_var(--line)]'
              )}
            >
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                aria-hidden="true"
              >
                <path
                  d={SHIRT_OUTLINE}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.4}
                  strokeLinejoin="round"
                />
                <rect
                  x={mark?.x}
                  y={mark?.y}
                  width={mark?.w}
                  height={mark?.h}
                  rx={0.6}
                  fill={isCustom ? 'none' : 'var(--cobalt-500)'}
                  stroke={isCustom ? 'var(--cobalt-500)' : 'none'}
                  strokeWidth={isCustom ? 1 : 0}
                  strokeDasharray={isCustom ? '1.5 1' : undefined}
                />
              </svg>
              <span className="dc-type-option text-center [overflow-wrap:anywhere]">
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
